import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { errMsg, useApi } from "@/lib/api";
import ChecksList from "./ChecksList";

export default function PublishDialog({ site, deploy, onOpenChange, onDone }) {
  const api = useApi();
  const [detail, setDetail] = useState(null);
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setDetail(null); setAck(false);
    if (deploy) api.get(`/sites/${site.id}/deploys/${deploy.id}`).then(({ data }) => setDetail(data)).catch((e) => toast.error(errMsg(e)));
  }, [api, site.id, deploy]);
  const rollback = deploy && site.live && deploy.number < site.live.number;
  const ch = detail?.checks;
  const blocked = ch?.errors?.length > 0;
  const needAck = ch?.warnings?.length > 0;
  const publish = async () => {
    setBusy(true);
    try {
      await api.post(`/sites/${site.id}/deploys/${deploy.id}/promote`, { target: "live", acknowledge_warnings: ack });
      toast.success(rollback ? `Ripristinata online la compilazione n. ${deploy.number}` : `Compilazione n. ${deploy.number} pubblicata online`);
      onDone(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!deploy} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[88vh] overflow-y-auto" data-testid="publish-dialog">
        <DialogHeader>
          <DialogTitle>{rollback ? "Ripristina online" : "Pubblica online"} la compilazione n. {deploy?.number}</DialogTitle>
          <DialogDescription>
            {rollback ? `La versione online passa dalla n. ${site.live.number} alla n. ${deploy?.number}. ` : site.live ? `Sostituisce la versione online n. ${site.live.number}. ` : ""}
            {site.hosting?.live_host
              ? `Viene pubblicata su «${site.hosting.label}» all'indirizzo ${site.hosting.server_only ? "http" : "https"}://${site.hosting.live_host} (indicizzabile).${site.hosting.server_only ? " Indirizzo di prova raggiungibile solo dal server." : ""} Il collegamento dei domini dei clienti non è ancora disponibile.`
              : "Con l'hosting interno la versione online resta visibile solo dallo Studio (noindex). Per pubblicarla su Internet scegli Cloudflare in «Hosting dei siti»."}
          </DialogDescription>
        </DialogHeader>
        {!detail ? <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={14} className="animate-spin" />Caricamento controlli…</p> : <ChecksList checks={ch} compact />}
        {detail && !blocked && !needAck && <p className="text-sm text-emerald-700" data-testid="publish-all-clear">Tutti i controlli sono superati.</p>}
        {blocked && <p className="text-sm text-red-700" data-testid="publish-blocked">Ci sono errori bloccanti: correggili nel progetto e ricompila.</p>}
        {needAck && !blocked && (
          <label className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="publish-ack-checkbox" />Ho letto gli avvisi e voglio pubblicare comunque
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="publish-cancel-btn">Annulla</Button>
          <Button onClick={publish} disabled={!detail || blocked || (needAck && !ack) || busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-confirm-btn">
            {busy && <Loader2 size={14} className="mr-1.5 animate-spin" />}{rollback ? "Ripristina online" : "Pubblica online"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
